import React from 'react';
import { useLanguage } from '@/lib/i18n';

const SEVERITY_COLORS = {
  0: 'bg-warmgray/30',
  1: 'bg-green-100',
  2: 'bg-green-200',
  3: 'bg-yellow-100',
  4: 'bg-yellow-200',
  5: 'bg-orange-100',
  6: 'bg-orange-200',
  7: 'bg-red-100',
  8: 'bg-red-200',
  9: 'bg-red-300',
  10: 'bg-red-400',
};

export function SymptomHeatmap({ data, metric, title, color = 'terracotta' }) {
  const { language } = useLanguage();
  const de = language === 'de';

  const last28 = Array.from({ length: 28 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (27 - i));
    const dateStr = d.toISOString().split('T')[0];
    const entry = data.find(e => e.date === dateStr);
    const val = entry ? (typeof metric === 'function' ? metric(entry) : entry[metric]) || 0 : 0;
    return { date: dateStr, day: d.getDate(), weekday: d.getDay(), val: Math.min(10, Math.round(val)) };
  });

  const weeks = [];
  for (let i = 0; i < last28.length; i += 7) {
    weeks.push(last28.slice(i, i + 7));
  }

  return (
    <div data-testid="symptom-heatmap">
      {title && <h3 className="font-display text-sm font-medium mb-3 text-foreground/70">{title}</h3>}
      <div className="flex gap-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1 flex-1">
            {week.map((day, di) => (
              <div
                key={di}
                className={`aspect-square rounded-md ${SEVERITY_COLORS[day.val]} flex items-center justify-center transition-all hover:scale-110`}
                title={`${day.date}: ${day.val}/10`}
                data-testid={`heatmap-cell-${day.date}`}
              >
                <span className="font-body text-[9px] text-foreground/40">{day.day}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="font-body text-[10px] text-foreground/40">{de ? 'Wenig' : 'Low'}</span>
        <div className="flex gap-0.5">
          {[0, 2, 4, 6, 8, 10].map(v => (
            <div key={v} className={`w-3 h-3 rounded-sm ${SEVERITY_COLORS[v]}`} />
          ))}
        </div>
        <span className="font-body text-[10px] text-foreground/40">{de ? 'Stark' : 'High'}</span>
      </div>
    </div>
  );
}

export function TriggerCorrelation({ history, triggers, symptoms, title }) {
  const { language } = useLanguage();
  const de = language === 'de';

  const correlations = triggers.map(trigger => {
    const withTrigger = history.filter(h => h.triggers?.includes(trigger));
    const withoutTrigger = history.filter(h => !h.triggers?.includes(trigger));

    const results = symptoms.map(sym => {
      const avgWith = withTrigger.length > 0 ? withTrigger.reduce((s, h) => s + (h[sym.key] || 0), 0) / withTrigger.length : 0;
      const avgWithout = withoutTrigger.length > 0 ? withoutTrigger.reduce((s, h) => s + (h[sym.key] || 0), 0) / withoutTrigger.length : 0;
      const diff = avgWith - avgWithout;
      const pct = avgWithout > 0 ? Math.round((diff / avgWithout) * 100) : 0;
      return { symptom: sym.label, diff, pct };
    }).filter(r => Math.abs(r.pct) > 10);

    return { trigger, count: withTrigger.length, correlations: results };
  }).filter(t => t.count > 0 && t.correlations.length > 0);

  if (correlations.length === 0) return null;

  return (
    <div data-testid="trigger-correlation">
      {title && <h3 className="font-display text-sm font-medium mb-3 text-foreground/70">{title}</h3>}
      <div className="space-y-2">
        {correlations.map((c, i) => (
          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-warmgray/30">
            <span className="font-body text-sm font-medium text-foreground/80 shrink-0 w-24">{c.trigger}</span>
            <div className="flex flex-wrap gap-1.5">
              {c.correlations.map((r, j) => (
                <span key={j} className={`px-2 py-0.5 rounded-full font-body text-[11px] font-medium ${r.pct > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {r.symptom} {r.pct > 0 ? '+' : ''}{r.pct}%
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
